import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { findOptionsForSelectFilter } from '../../util/search';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ListingLink } from '../../components';
import { EditListingDescriptionForm } from '../../forms';
import config from '../../config';

import css from './EditListingDescriptionPanel.module.css';

const EditListingDescriptionPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { description, title, publicData } = currentListing.attributes;
  const { category, vehicle } = publicData;

  const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
  const vehicleOptions = findOptionsForSelectFilter('vehicle', config.custom.filters);

  const InitialVehicleTypes = vehicleOptions.filter(v => (v.key === vehicle));
  const InitialVehicleType = !!InitialVehicleTypes.length ? InitialVehicleTypes[0] : vehicleOptions[0];

 const [listingTitle, setListingTitle] = useState(title);
 const [listingDescription, setListingDescription] = useState(description);
 const [vehicleType, setVehicleType] = useState(InitialVehicleType);

  const handleVehicleClick = v => {
    setVehicleType(v);
  };

  const vehicleCategories = vehicleType.key ? categoryOptions.reduce((acc, cat) => {
    if (!acc && cat.key === vehicleType.key) {
      acc = cat.subcategories;
    }
    return acc;
  }, null) : categoryOptions;

  const vehicleCategoryMaybe = vehicleCategories.map(c => (c.key)).indexOf(category) > -1 ? category : '';

  const compareDirtyUpdateInitialValues = formValues => {
    if (formValues.title !== listingTitle) {
      setListingTitle(formValues.title);
    }
    if (formValues.description !== listingDescription) {
      setListingDescription(formValues.description);
    }
  }

  const initialValues = {
    title: listingTitle,
    description: listingDescription,
    category: vehicleCategoryMaybe,
    vehicle: vehicleType.key
  };

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;

  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingDescriptionPanel.title"
      values={{ listingTitle: <ListingLink listing={listing} /> }}
    />
  ) : (
    <FormattedMessage id="EditListingDescriptionPanel.createListingTitle" />
  );

  return (
    <div className={classes}>
      <h1 className={css.title}>{panelTitle}</h1>
      <EditListingDescriptionForm
        className={css.form}
        initialValues={initialValues}
        saveActionMsg={submitButtonText}
        onChange={formState => {
          if (formState.dirty && Object.keys(formState.values).length > 0) {
            compareDirtyUpdateInitialValues(formState.values);
          }
        }}
        onSubmit={values => {
          const { title, description, category, vehicle } = values;
          const updateValues = {
            title: title.trim(),
            description,
            publicData: {
              ...publicData,
              category,
              vehicle
            },
          };
          onSubmit(updateValues);
        }}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        categories={vehicleCategories}
        vehicles={vehicleOptions}
        handleVehicleClick={handleVehicleClick}
      />
    </div>
  );
};

EditListingDescriptionPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
};

EditListingDescriptionPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingDescriptionPanel;
